import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AtualizarContatoEntrada,
  CampoDto,
  EntidadeCampo,
  ContatoDto,
  CriarCampoEntrada,
  CriarContatoEntrada,
  CriarEtapaEntrada,
  CriarFunilEntrada,
  CriarMotivoPerdaEntrada,
  CriarTagEntrada,
  FunilDto,
  AtualizarNegociacaoEntrada,
  CriarNegociacaoEntrada,
  MotivoPerdaDto,
  MoverNegociacaoEntrada,
  NegociacaoDto,
  Pagina,
  PreviaImportacao,
  ResultadoImportacao,
  ResumoEtapaNegociacao,
  ResumosDeLeadsDto,
  CotaIaDto,
  EstatisticasIaDto,
  ResultadoLoteIaDto,
  AnaliseIaDto,
  SelecaoDeContatos,
  TagDto,
  MovimentoDto,
} from '@crm/shared'
import { api, enviarArquivo } from './cliente'

export interface FiltroLeads {
  pagina: number
  tamanho: number
  busca?: string
  vendedorId?: string
  empresaId?: string
  semVendedor?: boolean
  comNegociacao?: boolean
  tagIds?: string[]
  campos?: Record<string, string>
  ordenar?: 'recentes' | 'nome'
}

/** Recorte do quadro: aqui o card é o ciclo, não o cliente. */
export interface FiltroNegocios {
  pagina: number
  tamanho: number
  funilId?: string
  etapaId?: string
  contatoId?: string
  vendedorId?: string
  status: 'aberta' | 'ganha' | 'perdida' | 'todas'
  busca?: string
  tagIds?: string[]
  /** Campo personalizado do contato, como no filtro de Contatos. */
  campos?: Record<string, string>
}

function paraQuery(filtro: FiltroLeads): Record<string, string | number | undefined> {
  const query: Record<string, string | number | undefined> = {
    pagina: filtro.pagina,
    tamanho: filtro.tamanho,
    busca: filtro.busca,
    vendedorId: filtro.vendedorId,
    empresaId: filtro.empresaId,
    ordenar: filtro.ordenar,
  }
  if (filtro.semVendedor) query.semVendedor = 'true'
  if (filtro.comNegociacao) query.comNegociacao = 'true'
  if (filtro.tagIds?.length) query.tagIds = filtro.tagIds.join(',')
  for (const [chave, valor] of Object.entries(filtro.campos ?? {})) {
    if (valor) query[`campos[${chave}]`] = valor
  }
  return query
}

export const chavesLeads = {
  contatos: (filtro: FiltroLeads) => ['contatos', filtro] as const,
  contato: (id: string) => ['contatos', id] as const,
  negociacoes: (filtro: FiltroNegocios) => ['negociacoes', filtro] as const,
  resumo: (filtro: FiltroNegocios) => ['negociacoes', 'resumo', filtro] as const,
  // [INÍCIO]/[FIM] ADR 0080
  resumos: (filtro: FiltroNegocios) => ['negociacoes', 'resumos', filtro] as const,
  // [INÍCIO]/[FIM] ADR 0081
  cotaIa: ['ia', 'cota'] as const,
  estatisticasIa: (de: string, ate: string) => ['ia', 'estatisticas', de, ate] as const,
  historico: (contatoId: string) => ['negociacoes', 'historico', contatoId] as const,
  campos: ['campos'] as const,
  funis: ['funis'] as const,
  tags: ['tags'] as const,
  motivos: ['motivos-perda'] as const,
}

// ─────────────────────────── contatos ───────────────────────────

export function useContatos(filtro: FiltroLeads, habilitado = true) {
  return useQuery({
    queryKey: chavesLeads.contatos(filtro),
    queryFn: () => api<Pagina<ContatoDto>>('/api/contatos', { query: paraQuery(filtro) }),
    enabled: habilitado,
  })
}

/**
 * O "Marcar tudo" da lista de contatos: todos do filtro, só id e etiquetas.
 * Chamado no clique, e não como consulta viva: a seleção é uma foto do
 * momento, e não deve mudar sozinha enquanto a pessoa escolhe a ação.
 */
export function buscarSelecaoDeContatos(filtro: FiltroLeads) {
  return api<SelecaoDeContatos>('/api/contatos/selecao', { query: paraQuery(filtro) })
}

// ─────────────────────────── negociações ───────────────────────────

function negocioParaQuery(filtro: FiltroNegocios): Record<string, string | number | undefined> {
  const query: Record<string, string | number | undefined> = {
    pagina: filtro.pagina,
    tamanho: filtro.tamanho,
    status: filtro.status,
    funilId: filtro.funilId,
    etapaId: filtro.etapaId,
    contatoId: filtro.contatoId,
    vendedorId: filtro.vendedorId,
    busca: filtro.busca,
    ...(filtro.tagIds?.length ? { tagIds: filtro.tagIds.join(',') } : {}),
  }
  for (const [chave, valor] of Object.entries(filtro.campos ?? {})) {
    if (valor) query[`campos[${chave}]`] = valor
  }
  return query
}

export function useNegociacoes(filtro: FiltroNegocios, habilitado = true) {
  return useQuery({
    queryKey: chavesLeads.negociacoes(filtro),
    queryFn: () =>
      api<Pagina<NegociacaoDto>>('/api/negociacoes', { query: negocioParaQuery(filtro) }),
    enabled: habilitado,
  })
}

export function useResumoEtapas(filtro: FiltroNegocios, habilitado = true) {
  return useQuery({
    queryKey: chavesLeads.resumo(filtro),
    queryFn: () =>
      api<ResumoEtapaNegociacao[]>('/api/negociacoes/resumo', { query: negocioParaQuery(filtro) }),
    enabled: habilitado,
  })
}

// [INÍCIO] ADR 0080: visão Resumos de Leads
export function useResumosDeLeads(filtro: FiltroNegocios, habilitado = true) {
  return useQuery({
    queryKey: chavesLeads.resumos(filtro),
    queryFn: () =>
      api<ResumosDeLeadsDto>('/api/negociacoes/resumos', { query: negocioParaQuery(filtro) }),
    enabled: habilitado,
  })
}
// [FIM] ADR 0080

// [INÍCIO] IA sob demanda e cota (ADR 0081)
export function useCotaIa(habilitado = true) {
  return useQuery({
    queryKey: chavesLeads.cotaIa,
    queryFn: () => api<CotaIaDto>('/api/ia/cota'),
    enabled: habilitado,
  })
}

export function useEstatisticasIa(de: string, ate: string, habilitado = true) {
  return useQuery({
    queryKey: chavesLeads.estatisticasIa(de, ate),
    queryFn: () => api<EstatisticasIaDto>('/api/ia/estatisticas', { query: { de, ate } }),
    enabled: habilitado && Boolean(de && ate),
  })
}

export function useGerarResumosIa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (corpo: {
      de: string
      ate: string
      funilId?: string
      chamadoIds?: string[]
      tamanhoLote?: number
      soPendentes?: boolean
    }) => api<ResultadoLoteIaDto>('/api/ia/resumos', { metodo: 'POST', corpo }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesLeads.cotaIa })
      void cliente.invalidateQueries({ queryKey: ['negociacoes'] })
    },
  })
}

export function useGerarAnaliseIa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (corpo: { de: string; ate: string }) =>
      api<AnaliseIaDto & { cota: CotaIaDto }>('/api/ia/analise', { metodo: 'POST', corpo }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ia'] })
    },
  })
}

export function useAjustarCotaIa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (corpo: {
      cotaIaResumos?: number
      cotaIaAnalises?: number
      extraResumos?: number
      extraAnalises?: number
    }) => api<CotaIaDto>('/api/ia/cota', { metodo: 'PATCH', corpo }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesLeads.cotaIa })
      void cliente.invalidateQueries({ queryKey: ['ia'] })
    },
  })
}
// [FIM] IA sob demanda e cota

/** Todos os ciclos de um contato, para a aba Histórico do card. */
export function useHistoricoDoContato(contatoId: string | null) {
  return useQuery({
    queryKey: chavesLeads.historico(contatoId ?? ''),
    queryFn: () =>
      api<Pagina<NegociacaoDto>>('/api/negociacoes', {
        query: { contatoId: contatoId as string, status: 'todas', tamanho: 100 },
      }),
    enabled: Boolean(contatoId),
  })
}

export function useContato(id: string | null) {
  return useQuery({
    queryKey: chavesLeads.contato(id ?? ''),
    queryFn: () => api<ContatoDto>(`/api/contatos/${id}`),
    enabled: Boolean(id),
  })
}

function invalidarContatos(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: ['contatos'] })
  /*
    A lista de conversas também: o funil e a etapa dos quadradinhos do alto
    da conversa vêm dela. Sem isto, criar a negociação pela conversa deixava
    os quadradinhos cinza até a lista recarregar por outro motivo (mapa de uso,
    08/10/2026).
  */
  void cliente.invalidateQueries({ queryKey: ['conversas'] })
  // Mexer no contato mexe no card: as duas árvores caem juntas, senão o
  // quadro fica mostrando o nome antigo depois de renomear o cliente.
  void cliente.invalidateQueries({ queryKey: ['negociacoes'] })
}

export function useCriarContato() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: Partial<CriarContatoEntrada>) =>
      api<ContatoDto>('/api/contatos', { metodo: 'POST', corpo: dados }),
    onSuccess: () => invalidarContatos(cliente),
  })
}

export function useAtualizarContato() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: AtualizarContatoEntrada }) =>
      api<ContatoDto>(`/api/contatos/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => invalidarContatos(cliente),
  })
}

/**
 * Etiquetas de um contato. Substitui o conjunto inteiro, não acrescenta uma.
 *
 * Invalida também as conversas: a etiqueta aparece no cartão do bate-papo, e
 * essa lista tem chave própria — sem isto o cartão continuaria sem ela até a
 * varredura de 30 segundos.
 */
export function useDefinirTags() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ contatoId, tagIds }: { contatoId: string; tagIds: string[] }) =>
      api<unknown>(`/api/contatos/${contatoId}/tags`, { metodo: 'PUT', corpo: { tagIds } }),
    onSuccess: () => {
      invalidarContatos(cliente)
      void cliente.invalidateQueries({ queryKey: ['conversas'] })
    },
  })
}

export function useMoverEtapa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: MoverNegociacaoEntrada }) =>
      api<NegociacaoDto>(`/api/negociacoes/${id}/etapa`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => invalidarContatos(cliente),
  })
}

export function useNovaNegociacao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarNegociacaoEntrada) =>
      api<NegociacaoDto>('/api/negociacoes', { metodo: 'POST', corpo: dados }),
    onSuccess: () => invalidarContatos(cliente),
  })
}

export function useAtualizarNegociacao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: AtualizarNegociacaoEntrada }) =>
      api<NegociacaoDto>(`/api/negociacoes/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => invalidarContatos(cliente),
  })
}

export function useExcluirContato() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/contatos/${id}`, { metodo: 'DELETE' }),
    onSuccess: (_resposta, id) => {
      // A ficha do apagado sai do cache antes: invalidada, ela ia buscar de novo e voltar 404.
      cliente.removeQueries({ queryKey: chavesLeads.contato(id), exact: true })
      invalidarContatos(cliente)
    },
  })
}

// ─────────────────────── campos customizados ───────────────────────

/** Os campos do contato (o padrão) ou da empresa (ADR 0069). */
export function useCampos(entidade: EntidadeCampo = 'contato') {
  return useQuery({
    queryKey: [...chavesLeads.campos, entidade],
    queryFn: () => api<CampoDto[]>('/api/campos', { query: { entidade } }),
  })
}

export function useCriarCampo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarCampoEntrada) =>
      api<CampoDto>('/api/campos', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.campos }),
  })
}

export function useAtualizarCampo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<CampoDto>(`/api/campos/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.campos }),
  })
}

export function useExcluirCampo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; desativado: boolean; fichasComValor: number }>(`/api/campos/${id}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.campos }),
  })
}

// ─────────────────────────── funis ───────────────────────────

export function useFunis() {
  return useQuery({ queryKey: chavesLeads.funis, queryFn: () => api<FunilDto[]>('/api/funis') })
}

export function useCriarFunil() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarFunilEntrada) =>
      api<{ id: string }>('/api/funis', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

export function useAtualizarFunil() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<unknown>(`/api/funis/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

export function useExcluirFunil() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/funis/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

export function useCriarEtapa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ funilId, dados }: { funilId: string; dados: CriarEtapaEntrada }) =>
      api<unknown>(`/api/funis/${funilId}/etapas`, { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

export function useAtualizarEtapa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<unknown>(`/api/etapas/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

export function useExcluirEtapa() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/etapas/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

export function useReordenarEtapas() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ funilId, etapaIds }: { funilId: string; etapaIds: string[] }) =>
      api<FunilDto[]>(`/api/funis/${funilId}/etapas/ordem`, {
        metodo: 'PUT',
        corpo: { etapaIds },
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.funis }),
  })
}

// ─────────────────── etiquetas e motivos de perda ───────────────────

export function useTags() {
  return useQuery({ queryKey: chavesLeads.tags, queryFn: () => api<TagDto[]>('/api/tags') })
}

export function useCriarTag() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarTagEntrada) =>
      api<TagDto>('/api/tags', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.tags }),
  })
}

export function useAtualizarTag() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<TagDto>(`/api/tags/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.tags }),
  })
}

export function useExcluirTag() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; contatosAfetados: number }>(`/api/tags/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesLeads.tags })
      void cliente.invalidateQueries({ queryKey: ['contatos'] })
    },
  })
}

export function useMotivosPerda() {
  return useQuery({
    queryKey: chavesLeads.motivos,
    queryFn: () => api<MotivoPerdaDto[]>('/api/motivos-perda'),
  })
}

export function useCriarMotivoPerda() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarMotivoPerdaEntrada) =>
      api<MotivoPerdaDto>('/api/motivos-perda', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.motivos }),
  })
}

export function useAtualizarMotivoPerda() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<MotivoPerdaDto>(`/api/motivos-perda/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.motivos }),
  })
}

export function useExcluirMotivoPerda() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; desativado: boolean }>(`/api/motivos-perda/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesLeads.motivos }),
  })
}

// ─────────────────────────── planilhas ───────────────────────────

/** Download direto: a rota é GET e o cookie de sessão vai junto. */
export function baixarPlanilha(caminho: string): void {
  window.location.href = caminho
}

/**
 * As rotas de uma planilha: `/api/planilhas` para contatos,
 * `/api/planilhas/empresas` para empresas (ADR 0039). As duas têm o mesmo
 * par prévia e confirmar.
 */
export function useEnviarPrevia(base: string) {
  return useMutation({
    mutationFn: (arquivo: File) => enviarArquivo<PreviaImportacao>(`${base}/previa`, arquivo),
  })
}

export function useConfirmarImportacao(base: string) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (token: string) =>
      api<ResultadoImportacao>(`${base}/confirmar`, {
        metodo: 'POST',
        corpo: { token },
      }),
    onSuccess: () => {
      invalidarContatos(cliente)
      // Empresa importada muda a lista de empresas e o dono dos contatos dela.
      void cliente.invalidateQueries({ queryKey: ['empresas'] })
    },
  })
}

/** Histórico de movimentações de uma negociação. Só carrega quando o card abre. */
export function useMovimentos(negociacaoId: string | null) {
  return useQuery({
    queryKey: ['negociacao-movimentos', negociacaoId ?? ''],
    queryFn: () => api<MovimentoDto[]>(`/api/negociacoes/${negociacaoId}/movimentos`),
    enabled: Boolean(negociacaoId),
  })
}
